import { create } from "zustand";
import type { Analysis, Arrangement, ChartMeasure, SongSource } from "../types/domain";
import { buildChartModel } from "../core/grid/buildChartModel";
import { buildChartTex } from "../core/notation/alphaTex";
import { applyTickMap } from "../core/timeMap";

interface ProjectState {
  title: string;
  analysis: Analysis | null;
  source: SongSource;
  beatLevelMul: 0.5 | 1 | 2;
  arrangement: Arrangement;

  // derived (recomputed by rebuild)
  chartMeasures: ChartMeasure[];
  tex: string;
  headerTempo: number | null;

  savedId: string | null;   // id of the saved score this project came from / was saved as (else null)

  loadAnalysis: (analysis: Analysis, source: SongSource, title: string) => void;
  loadProject: (p: { analysis: Analysis; source: SongSource; title: string; beatLevelMul: 0.5 | 1 | 2; arrangement: Arrangement; savedId?: string | null }) => void;
  setBeatLevelMul: (mul: 0.5 | 1 | 2) => void;
  setArrangement: (arr: Arrangement) => void;
  setSavedId: (id: string | null) => void;
  reset: () => void;
  rebuild: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  title: "",
  analysis: null,
  source: { kind: "default" },
  beatLevelMul: 1,
  arrangement: { measures: {} },
  chartMeasures: [],
  tex: "",
  headerTempo: null,
  savedId: null,

  loadAnalysis: (analysis, source, title) => {
    set({ analysis, source, title, beatLevelMul: 1, arrangement: { measures: {} }, savedId: null });
    get().rebuild();
  },
  loadProject: (p) => {
    set({ analysis: p.analysis, source: p.source, title: p.title, beatLevelMul: p.beatLevelMul,
          arrangement: p.arrangement || { measures: {} }, savedId: p.savedId ?? null });
    get().rebuild();
  },
  setBeatLevelMul: (mul) => { set({ beatLevelMul: mul }); get().rebuild(); },
  setArrangement: (arrangement) => { set({ arrangement }); get().rebuild(); },
  setSavedId: (savedId) => set({ savedId }),
  reset: () => set({
    title: "", analysis: null, source: { kind: "default" }, beatLevelMul: 1,
    arrangement: { measures: {} }, chartMeasures: [], tex: "", headerTempo: null, savedId: null,
  }),

  rebuild: () => {
    const { analysis, beatLevelMul, arrangement } = get();
    if (!analysis) { set({ chartMeasures: [], tex: "", headerTempo: null }); return; }
    const { measures, empty } = buildChartModel(analysis, beatLevelMul);
    if (empty) { set({ chartMeasures: [], tex: "", headerTempo: null }); return; }
    const { tex, tickMap, headerTempo } = buildChartTex({
      chartMeasures: measures, arrangement, beatLevelMul, analysisBpm: analysis.bpm,
    });
    set({ chartMeasures: applyTickMap(measures, tickMap), tex, headerTempo });
  },
}));
